import has from 'lodash/has';
import isEqual from 'lodash/isEqual';

import type { ScreenerReqFilters } from '@revelio/data-access';

import { isTaxonomyFilterId } from '../constants/filter-constants';
import {
  type Filter,
  type FilterId,
  type FilterSelectionsMap,
  FilterType,
  isSegmentedFilter,
  type ListFilter,
  type PrimarySelection,
  type RangeFilter,
  type SingleValueFilter,
  type StaticListFilter,
  type TreeFilter,
  type TreeSelection,
} from '../types/filters';
import { createSegmentedFilter } from '../utils/common';
import { canFilterBeSegmented } from '../utils/filterSegmentation';
import {
  removeSelection,
  updatePrimarySelections,
} from '../utils/primaryFilterUtils';
import { processFilterDependencies } from './filterDependencies';
import {
  addNewFilterGroup,
  applyFilterAction,
  type ConsistencyKey,
  type FilterConsistencyState,
  handleReplaceFilters,
  updatePrimarySelectionsInGroup,
} from './filterGroupConsistency';
import { getFilterDefinition } from './filterRegistry';
import {
  type MultiFilterAction,
  multiFilterReducer,
} from './multiFilterReducer';

export type FilterAction =
  | MultiFilterAction
  // Re-run the adaptive-taxonomy derivation (the base reducer no-ops; the
  // wrapper in FilterProvider derives after every action). Dispatched once the
  // async deps (RICS tree / taxonomy versions) load so a cold page can finalize.
  | { type: 'REDERIVE_ADAPTIVE_TAXONOMY' }
  | { type: 'SET_FILTER'; filter: Filter; groupKeyOverride?: ConsistencyKey }
  | {
      type: 'UPDATE_FILTER';
      filter: Partial<Filter> & { id: FilterId };
      groupKeyOverride?: ConsistencyKey;
      /**
       * Marks WHO is writing. `'preference-sync'` = an automated
       * user-preference sync (e.g. `SyncInitialPreferencesToFilters` /
       * `updateCustomDashFilters`), NOT a direct user edit. The reducer
       * skips a preference-sync write that targets a filter whose CURRENT
       * state is `isAiApplied` — AI-applied values are paired with
       * taxonomy-relative ids dispatched in the same apply, and the sync's
       * own effect-level `isAiApplied` guard reads a stale render-closure
       * filter map, so it cannot reliably protect a just-applied value.
       * Guarding here, where state is LIVE, is race-proof by construction.
       * Unmarked (user-initiated) updates still override normally.
       */
      source?: 'preference-sync';
    }
  | { type: 'REMOVE_FILTER'; filterId: FilterId }
  | {
      type: 'REPLACE_FILTERS';
      payload: {
        filterMap: FilterSelectionsMap;
        consistencyKey: ConsistencyKey;
      };
    }
  | {
      type: 'ADD_NEW_FILTER_GROUP';
      payload: {
        newFilterKey: ConsistencyKey;
        newFilterMap: FilterSelectionsMap;
      };
    }
  | {
      type: 'UPDATE_PRIMARY_SELECTIONS';
      payload: {
        filterId: FilterId;
        newSelections: TreeSelection[];
        limit: number;
        groupKeyOverride?: ConsistencyKey;
        appendLast?: boolean;
      };
    }
  | {
      type: 'REMOVE_PRIMARY_SELECTION';
      payload: { filterId: FilterId; selectionId: string };
    }
  | {
      type: 'REORDER_PRIMARY_SELECTIONS';
      payload: {
        filterId: FilterId;
        newPrimarySelections: PrimarySelection[];
      };
    }
  | {
      type: 'UPDATE_SEGMENT_IN_FILTER';
      payload: {
        filterId: FilterId;
        segmentKey: string;
        segmentFilter: Filter;
      };
    }
  | {
      type: 'REMOVE_SEGMENT_FROM_FILTER';
      payload: {
        filterId: FilterId;
        segmentKey: string;
      };
    }
  | {
      type: 'UPDATE_SEGMENTED_FILTER';
      payload: {
        filterId: FilterId;
        wrappedFilter: Filter;
        segmentations?: Partial<Record<keyof ScreenerReqFilters, Filter>>;
      };
    };

/**
 * Manages all filter state transitions, including setting, updating, and synchronizing filters.
 */
export const filterReducer = (
  state: FilterConsistencyState,
  action: FilterAction
): FilterConsistencyState => {
  switch (action.type) {
    case 'UPDATE_MULTI_FILTER_INSTANCE':
    case 'ADD_MULTI_FILTER_INSTANCE':
    case 'REMOVE_MULTI_FILTER_INSTANCE': {
      return multiFilterReducer(state, action);
    }

    case 'SET_FILTER':
    case 'UPDATE_FILTER': {
      // Preference-sync writes must never clobber an AI-applied value: the
      // AI apply pairs it (e.g. ROLE_TAXONOMY) with taxonomy-relative ids in
      // the same dispatch, and re-syncing the saved preference over it would
      // unpair the ids from their taxonomy (backend then resolves them to an
      // empty `role_v3_id` and rejects the request). A link-applied value
      // (`isLinkApplied`) is skipped too: it holds the sender's taxonomy,
      // which the viewer's own preference must not replace. The sync's
      // effect runs off a render-closure filter map that can be STALE
      // relative to a just-dispatched AI apply, so it cannot guard this
      // itself — the reducer sees LIVE state, so the guard lives here. Only
      // the targeted filter is skipped; the rest of the sync's batch (each
      // its own action) proceeds normally.
      if (
        action.type === 'UPDATE_FILTER' &&
        action.source === 'preference-sync'
      ) {
        const guardGroupKey = action.groupKeyOverride ?? state.activeKey;
        const currentFilter = guardGroupKey
          ? state.groupMap.get(guardGroupKey)?.get(action.filter.id)
          : undefined;
        if (
          currentFilter?.isAiApplied === true ||
          currentFilter?.isLinkApplied === true
        ) {
          return state;
        }
      }

      // ROLE, SKILL and ACTIVITY taxonomy updates will either clear dependent
      // filters or update defaults.
      //
      // Compare by taxonomy *identity* (selection payload), not by full object
      // equality including `version`. Callers like
      // `preference-filter-adapters.ts` dispatch updates without a `version`
      // field (user preferences don't carry taxonomy versions), which would
      // look like a value change compared to the filter-state value that has
      // `version` attached from the initial filter construction. That phantom
      // "change" would cascade through `processFilterDependencies` and wipe
      // downstream ROLE selections — e.g. on a geography page after an AI
      // apply dispatched ROLE_TAXONOMY `{default: true, version: '3.1.10'}`
      // and a pref sync immediately dispatched `{default: true}` (no version),
      // the ROLE filter (AI-applied "data scientists") would get cleared by
      // `filterDependencies.ts`' ROLE_TAXONOMY → ROLE dependency even though
      // the semantic taxonomy didn't change.
      const stripTaxonomyVersion = (v: unknown) => {
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          const { version: _dropVersion, ...rest } = v as Record<
            string,
            unknown
          >;
          return rest;
        }
        return v;
      };

      let taxonomyValueChanged = true;
      // `applyFilterAction` replaces the target filter wholesale. A partial
      // (value-less) taxonomy update must therefore be merged onto the
      // existing filter here, or its `type`/`value` would be dropped.
      let filterToApply = action.filter;
      if (
        action.type === 'UPDATE_FILTER' &&
        isTaxonomyFilterId(action.filter.id)
      ) {
        const activeMap = state.activeKey
          ? state.groupMap.get(state.activeKey)
          : undefined;
        const existing = activeMap?.get(action.filter.id);
        const incomingHasValue = has(action.filter, 'value');
        if (!incomingHasValue) {
          // Value-less update (e.g. re-selecting "Adaptive") only toggles
          // metadata — don't treat it as a taxonomy change that cascades into
          // ROLE / CUSTOM_SKILL / ACTIVITY.
          taxonomyValueChanged = false;
          if (existing) {
            // Merging onto the existing filter would silently RETAIN
            // `isAiApplied` and `isLinkApplied`. Any write reaching this merge
            // is user-initiated (a pref-sync onto either flag returned above),
            // and a genuine user change takes back control of the taxonomy —
            // clear both flags unless the action deliberately carries one.
            // (Value-CARRYING updates replace the filter wholesale via
            // `applyFilterAction(_, _, _, true)`, dropping the flags there.)
            filterToApply = {
              ...existing,
              ...action.filter,
              ...(existing.isAiApplied && !('isAiApplied' in action.filter)
                ? { isAiApplied: false }
                : {}),
              ...(existing.isLinkApplied && !('isLinkApplied' in action.filter)
                ? { isLinkApplied: false }
                : {}),
            } as Filter;
          }
        } else if (existing && existing.type === FilterType.VALUE) {
          const existingValue = (existing as SingleValueFilter<unknown>).value;
          const incomingValue = (
            action.filter as Partial<SingleValueFilter<unknown>>
          ).value;
          taxonomyValueChanged = !isEqual(
            stripTaxonomyVersion(existingValue),
            stripTaxonomyVersion(incomingValue)
          );
        }
      }

      // Segmentable filters must always be created as SegmentedFilter directly
      // If a non-segmented segmentable filter is passed to SET_FILTER, it's an error
      if (
        action.type === 'SET_FILTER' &&
        canFilterBeSegmented(action.filter) &&
        !isSegmentedFilter(action.filter)
      ) {
        console.error(
          `Attempted to SET_FILTER with a non-segmented segmentable filter (${action.filter.id}). Segmentable filters must be created as SegmentedFilter.`
        );
        return state;
      }

      const { nextState, updatedFilter } = applyFilterAction(
        state,
        action.filter.id,
        filterToApply,
        true,
        action?.groupKeyOverride
      );

      if (updatedFilter) {
        // only change dependent filters when taxonomy value changes
        if (isTaxonomyFilterId(updatedFilter.id) && !taxonomyValueChanged) {
          return nextState;
        }

        return processFilterDependencies(
          nextState,
          updatedFilter.id,
          updatedFilter
        );
      }

      return nextState;
    }

    case 'REMOVE_FILTER': {
      const defaultFilter = getFilterDefinition(action.filterId);
      if (!defaultFilter) {
        return state;
      }

      const { nextState, updatedFilter } = applyFilterAction(
        state,
        action.filterId,
        defaultFilter,
        true
      );

      if (updatedFilter) {
        return processFilterDependencies(
          nextState,
          action.filterId,
          updatedFilter
        );
      }

      return nextState;
    }

    case 'REPLACE_FILTERS': {
      return handleReplaceFilters(state, action.payload);
    }

    case 'ADD_NEW_FILTER_GROUP': {
      return addNewFilterGroup({
        filterState: state,
        newFilterKey: action.payload.newFilterKey,
        newFilterMap: action.payload.newFilterMap,
      });
    }

    case 'REMOVE_PRIMARY_SELECTION': {
      const { selectionId } = action.payload;
      return updatePrimarySelectionsInGroup(
        state,
        action.payload,
        (currentSelections) => {
          const { canRemove, updatedSelections } = removeSelection(
            currentSelections,
            selectionId
          );
          // Only update if removal is allowed
          return canRemove ? updatedSelections : null;
        }
      );
    }

    case 'REORDER_PRIMARY_SELECTIONS': {
      const { newPrimarySelections } = action.payload;
      return updatePrimarySelectionsInGroup(
        state,
        action.payload,
        () => newPrimarySelections
      );
    }

    case 'UPDATE_PRIMARY_SELECTIONS': {
      const { newSelections, limit, appendLast } = action.payload;
      return updatePrimarySelectionsInGroup(
        state,
        action.payload,
        (currentSelections) => {
          return updatePrimarySelections(
            newSelections,
            currentSelections,
            limit,
            appendLast
          );
        }
      );
    }

    case 'UPDATE_SEGMENT_IN_FILTER': {
      const { filterId, segmentKey, segmentFilter } = action.payload;
      const activeMap = state.activeKey
        ? state.groupMap.get(state.activeKey)
        : undefined;
      const existingFilter = activeMap?.get(filterId);

      if (!existingFilter || !isSegmentedFilter(existingFilter)) {
        return state;
      }

      const updatedSegmentations = {
        ...(existingFilter.segmentations || {}),
        [segmentKey]: segmentFilter,
      };

      const segmentedFilter = createSegmentedFilter({
        wrappedFilter: existingFilter.wrappedFilter,
        baseProps: existingFilter,
        segmentations: updatedSegmentations,
      });

      return applyFilterAction(state, filterId, segmentedFilter, true)
        .nextState;
    }

    case 'REMOVE_SEGMENT_FROM_FILTER': {
      const { filterId, segmentKey } = action.payload;
      const activeMap = state.activeKey
        ? state.groupMap.get(state.activeKey)
        : undefined;
      const existingFilter = activeMap?.get(filterId);

      if (!existingFilter || !isSegmentedFilter(existingFilter)) {
        return state;
      }

      const existingSegmentations = existingFilter.segmentations || {};
      const updatedSegmentations = Object.fromEntries(
        Object.entries(existingSegmentations).filter(
          ([key]) => key !== segmentKey
        )
      );

      const segmentedFilter = createSegmentedFilter({
        wrappedFilter: existingFilter.wrappedFilter,
        baseProps: existingFilter,
        segmentations: updatedSegmentations,
      });

      return applyFilterAction(state, filterId, segmentedFilter, true)
        .nextState;
    }

    case 'UPDATE_SEGMENTED_FILTER': {
      const { filterId, wrappedFilter, segmentations } = action.payload;
      const activeMap = state.activeKey
        ? state.groupMap.get(state.activeKey)
        : undefined;
      const existingFilter = activeMap?.get(filterId);

      // Ensure wrappedFilter is a segmentable type
      if (
        wrappedFilter.type !== FilterType.TREE &&
        wrappedFilter.type !== FilterType.LIST &&
        wrappedFilter.type !== FilterType.STATIC_LIST &&
        wrappedFilter.type !== FilterType.RANGE
      ) {
        return state;
      }

      // Type-safe creation/update of SegmentedFilter based on the wrapped filter type
      const baseProps =
        existingFilter && isSegmentedFilter(existingFilter)
          ? existingFilter // Preserve BaseFilter properties (like isUserSubmitted)
          : wrappedFilter; // New filter - wrappedFilter already has all BaseFilter properties

      const existingSegmentations =
        existingFilter && isSegmentedFilter(existingFilter)
          ? existingFilter.segmentations
          : undefined;
      const finalSegmentations = segmentations ?? existingSegmentations ?? {};

      const segmentedFilter = createSegmentedFilter({
        wrappedFilter: wrappedFilter as
          | TreeFilter
          | ListFilter
          | StaticListFilter
          | RangeFilter,
        baseProps,
        segmentations: finalSegmentations,
      });

      return applyFilterAction(state, filterId, segmentedFilter, true)
        .nextState;
    }

    default:
      return state;
  }
};
